<template>
  <vue-form :state="formState" class="step-one" id="step-one">
    <h2 class="fs-24 font-smoothing">Bookingoplysninger</h2>
    
    <!-- Error message for airports data issues -->
    <div v-if="airportsDataError" class="alert alert-error mb-4 p-3" style="background-color: #ffe6e6; border: 1px solid #ff4444; border-radius: 4px;">
      <p class="color-red fs-16 font-smoothing mb-2">
        <strong>Problem med indlæsning af lufthavnsdata</strong>
      </p>
      <p class="color-red fs-14 font-smoothing mb-2">
        Data kunne ikke indlæses korrekt. Prøv at genindlæse siden (Ctrl+Shift+R).
      </p>
      <p class="color-red fs-12 font-smoothing">
        Hvis problemet fortsætter, skal du rydde browserens cache og prøve igen.
      </p>
    </div>
    
    <step>
      <!-- Service Type Selection -->
      <div class="service-type-selection mb-4">
        <p class="color-grey-3 font-smoothing fs-14 mb-3">Vælg mellem at få hentet din bagage hjemme OG/ELLER at få bagagen fragtet hjem fra Københavns Lufthavn uden at vente ved bagagebåndet (når din rejse er indenfor EU toldområde)</p>
        
        <div class="service-options">
          <boolean-slider class="mb-4" label="Udrejse: Få hentet bagagen hjemme med check-in" v-model="serviceUdrejse" size="18"/>
          <p class="color-grey-3 fs-14 font-smoothing mb-4">Vi henter bagagen hos dig og tjekker dig ind som du kender det hvis du stod i skranken i lufthavnen</p>

        </div>
      </div>

      <!-- Basic Booking Information - Only shown when Udrejse is selected -->
      <div v-if="serviceUdrejse" class="basic-booking-info mb-4">
        <h3 class="fs-20 font-smoothing mb-4 color-blue">Bookingoplysninger</h3>
        
      <div class="d-block d-lg-flex">
        <div class="w-100 w-lg-50">
          <p class="color-grey-3 font-smoothing fs-14 mb-2">{{ dateFieldLabel }} <span class="asterisk">*</span></p>
          <v-date-picker
            v-model="date"
            locale="da-DK"
            :min-date="firstAvailableDate()"
            :disabled-dates="disabledDates"
            :masks="dateMasks"
          >
            <template v-slot="{ inputValue, inputEvents }">
              <input
                class="input-default w-100"
                :class="{'error-border' : form_valid_map.date.showError }"
                placeholder="DD-MM-YYYY"
                :value="inputValue"
                readonly
                v-on="inputEvents"
              >
            </template>
          </v-date-picker>
          <p class="color-grey-3 font-smoothing fs-12 mt-2" v-if="date_less_than_72_hours || this.prices.priceAddition >0">{{date_warning_text}}</p>
          <p class="italic color-red2 font-smoothing fs-12 mb-2" v-if="form_valid_map.date.showError">{{ form_valid_map.date.errorTexts.join(' ') }}</p>
        </div>
        <div class="w-100 w-lg-50 mt-4 mt-lg-0">
          <p class="color-grey-3 font-smoothing fs-14 mb-2">{{ timeFieldLabel }} <span class="asterisk">*</span></p>
          <vue-timepicker v-model="time" format="HH:mm" :minute-interval="5" :class="{'error-border' : form_valid_map.time.showError}"></vue-timepicker>
        </div>
      </div>

      <div class="d-block d-lg-flex my-4">
        <validate tag="div" class="w-100 pr-4 mt-4 mt-lg-0">
          <p class="color-grey-3 font-smoothing fs-14 mb-2">{{ flightNumberLabel }}<span class="asterisk">*</span></p>
          <input required name="flightNumber" class="input-default w-100" type="text" placeholder="eks. LH 2439" v-model.trim="flightNumber" @change="handleFlightNumberChange" :class="{'error-border' : form_valid_map.flightNumber.showError}">
          <p class="italic color-red2 font-smoothing fs-12 mb-2" v-if="form_valid_map.flightNumber.showError">{{ form_valid_map.flightNumber.errorTexts.join(' ') }}</p>
        </validate>

        <div class="w-100 mt-4 mt-lg-0">
          <p class="color-grey-3 font-smoothing fs-14 mb-2">{{ destinationFieldLabel }}<span class="asterisk">*</span></p>
          <AutocompleteSelect
            v-model="selectedDestination"
            :options="airportsSafe"
            :label="destinationLabel"
            track-by="iata"
            placeholder="f.eks: PMI eller Mallorca"
            :allow-empty="false"
            :show-suggestions-on-focus="true"
            :require-selection="true"
            :invalid="form_valid_map.destination.showError"
            selection-error-text="Vælg en lufthavn fra listen – teksten alene kan ikke bruges."
            no-results-text="Ingen lufthavn fundet. Prøv navn, by eller IATA-kode."
            @select="selectDestination"
            @clear="clearDestination"
          />
          <validate tag="div">
            <input v-show="false" v-model="destination" required name="destination" />
          </validate>
        </div>
      </div>

	<div class="d-block d-lg-flex my-4">
		<validate tag="div" class="w-100 pr-4 mt-4 mt-lg-0">
			<p class="color-grey-3 font-smoothing fs-14 mb-2">Indtast booking / billetnummer <span class="asterisk">*</span></p>
			<input required name="number" class="input-default w-100" type="text" v-model="number" :class="{'error-border' : form_valid_map.number.showError}">
			<p class="color-grey-3 font-smoothing fs-12 mb-2 mt-2"></p>
      <p class="color-red2 font-smoothing fs-12 mb-2" v-if="form_valid_map.number.showError">{{ form_valid_map.number.errorTexts.join(' ') }}</p>
		</validate>

		<validate tag="div" class="w-100 mt-4 mt-lg-0" >
			<p class="color-grey-3 font-smoothing fs-14 mb-2">Angiv evt. hvor du har købt billetten</p>
			<input name="travelagency" class="input-default w-100" type="text" v-model="travelagency" :class="{'error-border' : form_valid_map.travelagency.showError}" placeholder ="eks. Spies, Apollo, sas.dk eller anden">
			<p class="color-grey-3 font-smoothing fs-12 mb-2 mt-2">Du kan udelade feltet, men det kan være en hjælp for os.</p>
		</validate>
	</div>

      </div>

      <boolean-slider class="mb-4" label="Hjemrejse - uden at vente på bagagen i København" v-model="serviceHjemrejse" size="18" :disabled="isHjemrejseDisabled"/>
      <p class="color-grey-3 fs-14 font-smoothing">Vi kan tilbyde at hjælpe med at transportere din bagage hjem når du lander i København igen.</p>
      
      <p v-if="isHjemrejseDisabled && serviceUdrejse" class="color-red fs-14 font-smoothing mb-3">Hjemrejse er ikke tilgængelig for den valgte destination, da den ikke tillader toldservice.</p>

      <!-- Hjemrejse Section -->
      <div v-if="serviceHjemrejse" class="hjemrejse-section">
        <h3 class="fs-20 font-smoothing mb-4 color-blue">Hjemrejse - uden at vente på bagagen i København</h3>
        

        
        <p class="color-grey-3 fs-14 font-smoothing mb-3">VALIZO kan tage din bagage direkte fra bagagebåndet, gennem tolden og hjem til dig. Så behøver du ikke selv vente på den, og vi vil bringe den hjem til dig dagen efter.</p>
        <p class="color-grey-3 fs-14 font-smoothing">Det gælder dog nogle myndighedsrestriktioner og krav fra os for at det kan lade sig gøre, som du skal acceptere senere. Bla skal:</p>
        
        <div class="checkbox-wrapper mb-3">
          <input 
            type="checkbox" 
            id="customs-service-checkbox" 
            v-model="customsService"
          >
          <label for="customs-service-checkbox" class="fs-14 font-smoothing ml-2">
            *din rejse skal være indenfor EU og må ikke være startet udenfor EU. Bemærk det ikke gælder de Kanariske øer, Færøerne, Grønland, Nord Cypern)
            <a href="https://toldstyrelsen.dk/borger/rejser/rejser-inden-for-eu" target="_blank" class="color-blue ml-2">https://toldstyrelsen.dk/borger/rejser/rejser-inden-for-eu</a>
          </label>
        </div>



        <div v-if="customsService" id="included_home">
        <div class="d-block d-lg-flex">
          <div class="w-100 w-lg-50">
            <p class="color-grey-3 font-smoothing fs-14 mb-2">Vælg ankomstdato i København</p>
            <v-date-picker
              v-model="dateHome"
              locale="da-DK"
              :min-date="firstAvailableDate()"
              :disabled-dates="disabledDates"
              :masks="dateMasks"
            >
              <template v-slot="{ inputValue, inputEvents }">
                <input
                  class="input-default w-100"
                  placeholder="DD-MM-YYYY"
                  :value="inputValue"
                  readonly
                  v-on="inputEvents"
                >
              </template>
            </v-date-picker>
          </div>
          <div class="w-100 w-lg-50 mt-4 mt-lg-0">
            <p class="color-grey-3 font-smoothing fs-14 mb-2">Vælg ankomsttid i København</p>
            <vue-timepicker v-model="timeHome" format="HH:mm" :minute-interval="5"></vue-timepicker>
            <p class="italic color-red font-smoothing fs-12 mb-2" v-if="!customsService && !isNonEuTravel">Mellem kl. 20:00 og 6:30 er der en ekstra pris på DKK 500</p>
            <p class="italic color-red font-smoothing fs-12 mb-2" v-if="!customsService && isNonEuTravel">Mellem kl. 20:00 og 6:30 er der en ekstra pris på DKK 500</p>
            <p class="italic color-red font-smoothing fs-12 mb-2" v-if="customsService && isEuTravelOnly">Mellem kl. 20:00 og 6:30 eller efter 20:00 ændres pris til DKK 300</p>
          </div>
        </div>

        <div class="d-block d-lg-flex my-4">
          <validate tag="div" class="w-100 mt-4 mt-lg-0">
            <p class="color-grey-3 font-smoothing fs-14 mb-2">Flynummer som du lander med i København</p>
            <input required name="flightNumberHome" class="input-default w-100" type="text" placeholder="eks. LH 2439" v-model="flightNumberHome" :class="{'error-border' : form_valid_map.flightNumberHome.showError}">
          </validate>
        </div>

        <validate tag="div">
          <p class="color-grey-3 font-smoothing fs-14 mb-2 mt-4">Tilføj en kommentar</p>
          <textarea rows="4" class="w-100 input-default" v-model="commentHome" placeholder="Angiv feks. hvor vi må stille bagagen hvis du ikke er hjemme" />
        </validate>

        <!-- Customs Service Component -->
        <customs-service 
          v-if="customsService"
          :show-customs-service="customsService"
          @acceptance-change="handleCustomsAcceptanceChange"
          @validation-change="handleCustomsValidationChange"
        />

        </div>
      </div>
    </step>
  </vue-form>
</template>

<script>
import { mapFields } from 'vuex-map-fields'
import { mapGetters } from 'vuex'
import ClickOutside from 'vue-click-outside';
import { page } from 'vue-analytics'
import VueTimepicker from 'vue2-timepicker'
import Step from '@/components/Step'
import BooleanSlider from '@/components/BooleanSlider'
import CustomsService from '@/components/CustomsService'
import AutocompleteSelect from '@/components/AutocompleteSelect'
import { getAirports } from "../assets/airports";
import { checkAirportsDataVersion, validateAirportsData } from "../utils/airportsVersionCheck";
//import { getTravelagency } from "../assets/travelagency"; /*opslag på rejsebureauer, cka */


export default {
  name: 'StepOne',

  directives: {
    ClickOutside
  },

  components: {
    VueTimepicker,
    Step,
    BooleanSlider,
    AutocompleteSelect,
    CustomsService
  },

  data() {
    return {
      date_valid: false,
      flight_number_valid: false,
      booking_number_valid: false,
      airportsDataError: false,
      dateMasks: {
        input: 'DD-MM-YYYY'
      },

      flight_companies: {
        BI: 'Braathens International Airways', // BIX
        DK: 'Sunclass Airlines',
        DX: 'Danish Air Transport (DAT)',
        GL: 'Air Greenland',
        LH: 'Lufthansa',
        LX: 'Swiss Airlines',
        OS: 'Austrian Airlines',
        SK: 'SAS',
        N9: 'Novair',
        NV: 'Novair', // NVR
        JP: 'Jettime',
        JT: 'Jettime',
        KL: 'KLM',
        AF: 'Air France',
        RC: 'Atlantic Airways',
        SQ: 'Singapore Airlines',
        TT: 'Braathens International Airways'
      },
      formState: {},
      airports: [],
      selectedDestination: null,
      unavailable_flight_numbers: ["SK901x", "SK909x", "SK925x", "SK927x", "SK935x", "SK943x", "SK953x",
        "SK0901x", "SK0909x", "SK0925x", "SK0927x", "SK0935x", "SK0943x", "SK0953x"],
      date_less_than_72_hours: false,
      form_valid_map: {
        flightNumber: { isValid: true, errorTexts: [], showError: false },
        flightNumberHome: { isValid: true, errorTexts: [], showError: false },
        destination: { isValid: true, errorTexts: [], showError: false },
        number: { isValid: true, errorTexts: [], showError: false },
        time: { isValid: true, errorTexts: [], showError: false },
        date: { isValid: true, errorTexts: [], showError: false },
        travelagency: { isValid: true, errorTexts: [], showError: false }
      },
      staticDisabledDates: [
        new Date(2023,0,1), // 1 january 2023
        new Date(2023,3,6), // 6 april
        new Date(2023,3,7), // 7 april
        new Date(2023,3,9), // 9 april
        new Date(2023,3,10) // 10 april
      ],
      staticDisabledRanges: [
        {
          from: new Date(2024, 8, 12), // sept 12 included
          to: new Date(2024, 8, 16) // sept 16 not included
        }
      ]
    }
  },

  computed: {
    ...mapFields([
      `booking.number`,
      `booking.date`,
      `booking.time`,
      `booking.airport`,
      `booking.destination`,
      `booking.iataCode`,
      `booking.airline`,
      `booking.flightNumber`,
      `booking.travelagency`,
      `bookingHome.dateHome`,
      `bookingHome.timeHome`,
      `bookingHome.flightNumberHome`,
      `bookingHome.commentHome`,
      `bookingHome.customsService`,
      `bookingHome.customsAcceptance`,
      `bookingHome.customsValidation`,
      `bookingHome.isEuTravelOnly`,
      `bookingHome.isNonEuTravel`,
      `bookingHome.travelType`,
      `cart.includeHome`,
      `cart.serviceUdrejse`,
      `cart.serviceHjemrejse`,
      'shouldDisplayErrors',
      'booking.datetimeTouched'
    ]),
    ...mapGetters(['prices']),

    disabledDates() {
      const ranges = this.staticDisabledRanges.map((range) => {
        return { start: range.from, end: range.to };
      });
      return [...this.staticDisabledDates, ...ranges];
    },
    airportsSafe() {
      return Array.isArray(this.airports) ? this.airports : [];
    },

    invalid() {
      return this.formState ? this.formState.$invalid : false;
    },
    date_warning_text() {
      if (this.date_less_than_72_hours) {
        if (this.prices.priceAddition >= 300) {
            return "VI KAN IKKE GARANTERE DIN ORDRE MED KORT VARSEL, RING VENLIGST TIL KONTORET FOR AT FÅ EN BEKRÆFTELSE."
        } else if (this.prices.priceAddition >= 150) {
            return "Da der er mindre end 72 timer til din afrejse er der et tillæg på DKK 150 for en hasteordre."
        }
      } else if (this.prices.priceAddition > 300) {
        return "Grundet Covid19 restriktioner er der tillæg for afrejse i februar og marts."
      }
      
      return ""
    },

    isHjemrejseDisabled() {
      // If Udrejse is selected, check destination's customs capability
      if (this.serviceUdrejse) {
        if (!this.selectedDestination) {
          return true;
        }
        return this.selectedDestination.eucustoms === false;
      }
      // If only Hjemrejse is selected, don't disable based on destination
      // since the destination field represents departure airport in this case
      return false;
    },

    destinationFieldLabel() {
      // If only Hjemrejse is selected, change label to "Afrejselufthavn"
      if (this.serviceHjemrejse && !this.serviceUdrejse) {
        return "Afrejselufthavn:";
      }
      return "Lufhavn hvor rejsen ender:";
    },

    flightNumberLabel() {
      // If only Hjemrejse is selected, change label to be more generic
      if (this.serviceHjemrejse && !this.serviceUdrejse) {
        return "Flynummer til destinationen";
      }
      return "Flynummer ud af København";
    },

    dateFieldLabel() {
      if (this.serviceHjemrejse && !this.serviceUdrejse) {
        return "Dato for udrejse til destinationen";
      }
      return "Afrejsedato";
    },

    timeFieldLabel() {
      if (this.serviceHjemrejse && !this.serviceUdrejse) {
        return "Tidspunkt for udrejse til destinationen";
      }
      return "Afrejsetidspunkt";
    },


  },

  watch: {
    $route (to){
      if (typeof to.query.post_n !== 'undefined') {
        this.contact.address.zip = to.query.post_n;
      }

      if (typeof to.query.amt_people !== 'undefined' && this.step === 1) {
        this.setPeople(to.query.amt_people);
      }

      // Enhanced test mode activation
      if (typeof to.query.vzoman !== 'undefined' || this.shouldActivateTestMode()) {
        this.payment.type = 'Mobilepay'
      }

    },
    date() {
      this.calculateDateWarning(this.date, this.time, true);
      this.checkStepValid();
    },
    time: {
      deep: true,
      handler(newTime) {
        this.calculateDateWarning(this.date, newTime);
        this.checkStepValid();
        this.form_valid_map.time.showError = false;
      }
    },
    destination() {
      this.checkStepValid();
    },
    flightNumber() {
      this.checkStepValid();
    },
    number() {
      this.checkStepValid();
          },
      dateHome() {
      this.checkStepValid();
    },
    timeHome: {
      deep: true,
      handler() {
        this.checkStepValid();
      }
    },
    flightNumberHome() {
      this.checkStepValid();
    },
    commentHome() {
      // No validation needed for comments
    },
    customsService() {
      this.checkStepValid();
    },
    serviceUdrejse() {
      this.$nextTick(() => this.checkStepValid());
    },
    serviceHjemrejse(newValue) {
      // Update the legacy includeHome for compatibility
      this.includeHome = newValue;
      
      if (!newValue) {
        // Reset responsible person when Hjemrejse is disabled
        this.$store.commit('setResponsiblePerson', null);
        
        // Clear mapped fields
        this.customsService = false;
        this.dateHome = null;
        this.timeHome = { HH: "12", mm: "45" };
        this.flightNumberHome = null;
        this.commentHome = null;
        this.customsAcceptance = false;
        this.customsValidation = false;
        this.travelType = 'non-eu';
        this.isEuTravelOnly = false;
        this.isNonEuTravel = true;
        
        // Clear fields directly in store (not mapped in this component)
        this.$store.state.bookingHome.departureAirport = null;
        this.$store.state.bookingHome.hasTransitStops = null;
        this.$store.state.bookingHome.travelersUnder16 = 0;
        this.$store.state.bookingHome.travelersAge16 = 0;
        this.$store.state.bookingHome.travelersAge17 = 0;
        this.$store.state.bookingHome.travelersAge18Plus = 0;
        this.$store.state.bookingHome.customsCode = '';
        this.$store.state.bookingHome.customsComment = '';
        
        // Clear transit stops array
        this.$store.commit('updateTransitStops', []);
      }
      this.checkStepValid();
    },
    includeHome(newValue) {
      // Sync with serviceHjemrejse for backward compatibility
      this.serviceHjemrejse = newValue;
    },
    customsAcceptance() {
    },
    customsValidation() {
    },
    travelType() {
    },
    isEuTravelOnly() {
    },
    isNonEuTravel() {
    },
    selectedDestination() {
      // Only disable serviceHjemrejse based on customs if Udrejse is also selected
      // When only Hjemrejse is selected, destination represents departure airport
      if (this.serviceUdrejse && this.serviceHjemrejse) {
        if (!this.selectedDestination || (this.selectedDestination && this.selectedDestination.eucustoms === false)) {
          this.serviceHjemrejse = false;
        }
      }
    },
    '$store.state.bookingHome.departureAirport'() {
      // Watch for changes in departure airport selection
      this.checkStepValid();
    }
  },

  methods: {
    destinationLabel: (item) => {
      if (!item || typeof item !== 'object') return '';
      return `${item.name || ''}(${item.iata || ''}), ${item.cityDK || ''}, ${item.countryDK || ''}`;
    },
    logBookingHomeState() {
    },
    checkStepValid() {
      // Require at least one service type to be selected
      if (!this.serviceUdrejse && !this.serviceHjemrejse) {
        this.$emit('invalid');
        return;
      }

      // For Udrejse service, we need basic booking information
      if (this.serviceUdrejse) {
        // Do date validation
        this.checkDate();
        // Do time validation
        this.checkTime();
        // Do flight_number validation
        this.checkFlightNumber();
        // Do destination validation
        this.checkDestination();
        // Do number validation
        this.checkNumber(); 
      }

      let formValid = true;
      
      // Only check basic form validation if Udrejse is selected
      if (this.serviceUdrejse) {
        for (let key in this.form_valid_map) {
          if (!this.form_valid_map[key].isValid) {
            formValid = false;
            break;
          }
        }
      }
      
      // Additional validation for return journey
      if (this.serviceHjemrejse) {
        // Require customs service checkbox to be checked when return journey is enabled
        if (!this.customsService) {
          formValid = false;
        }
        // If customs service is enabled, also check all required return journey fields
        else {
          // Check customs acceptance and validation
          if (!this.customsAcceptance || !this.customsValidation) {
            formValid = false;
          }
          
          // Check required return journey fields
          if (!this.dateHome) {
            formValid = false;
          }
          
          if (!this.timeHome || !this.timeHome.HH || !this.timeHome.mm) {
            formValid = false;
          }
          
          if (!this.flightNumberHome || this.flightNumberHome.trim() === '') {
            formValid = false;
          }
          
          // Check departure airport (from store since it's not mapped)
          if (!this.$store.state.bookingHome.departureAirport) {
            formValid = false;
          }
        }
      }

      // Log complete state on validation
      this.logBookingHomeState();

      if (formValid) {
        this.$emit('valid');
      } else {
        this.$emit('invalid');
      }
    },
    checkDate() {
      if (this.date != null) {
        this.form_valid_map.date.isValid = true
        this.form_valid_map.date.showError = false
        this.form_valid_map.date.errorTexts = [];
      } else {
        this.form_valid_map.date.isValid = false
        this.form_valid_map.date.errorTexts = ["Afrejsedato skal udfyldes"];
      }
    },
    checkTime() {
      if (this.time.HH != "" && this.time.mm != "") {
        this.form_valid_map.time.isValid = true
        this.form_valid_map.time.errorTexts = [];
      } else {
        this.form_valid_map.time.isValid = false
        this.form_valid_map.time.errorTexts = ["Afrejsetidspunkt skal udfyldes"];
      }
    },
    checkFlightNumber() {
      const flightNumberState = this.formState && this.formState['flightNumber'];
      const showErrorIfDirty = flightNumberState ? flightNumberState.$dirty : false;

      if (!this.flightNumber) {
        this.form_valid_map.flightNumber.isValid = false;
        this.form_valid_map.flightNumber.showError = showErrorIfDirty;
        this.form_valid_map.flightNumber.errorTexts = [ "Flynummer skal udfyldes" ];
        return;
      }

      let flight_number = this.flightNumber.toUpperCase();
      
      if (this.unavailable_flight_numbers.includes(flight_number)) {
        this.form_valid_map.flightNumber.isValid = false;
        this.form_valid_map.flightNumber.showError = showErrorIfDirty;
        this.form_valid_map.flightNumber.errorTexts = [ "Vi understøtter ikke check-in til USA pt., men vi kan hente din bagage og udlevere den til dig i lufthavnen" ];
        return;
      }

      if (!this.validFlightNumber(flight_number.replace(/\s+/g, '').substring(0,2))) {
        let flight_initials = flight_number.replace(/\s+/g, '').substring(0,2);//CKA

        this.form_valid_map.flightNumber.isValid = true; // dette er kun en warning
        this.form_valid_map.flightNumber.showError = showErrorIfDirty;
        this.form_valid_map.flightNumber.errorTexts = [ "Du har valgt flyselskabet " + flight_initials + " som vi ikke samarbejder med endnu. " +
          "Men vi kan stadig tilbyde at hente din baggage og så udlevere den til dig i lufthavnen, " +
          "men endnu ikke med check-in på din hjemmeadresse. Bemærk at vi mødes med dig ved skranken ca 2 timer før afgang, og vi forbereder boardingkort til dig og indleverer bagagen når vi mødes. Denne service koster kr 250 ekstra som du vil blive opkrævet efterfølgende." ]
        return;
      }

      this.form_valid_map.flightNumber.isValid = true
      this.form_valid_map.flightNumber.showError = false;
      this.form_valid_map.flightNumber.errorTexts = [];

    },
    checkNumber() {
      const numberState = this.formState && this.formState['number'];
      const showErrorIfDirty = numberState ? numberState.$dirty : false;

      if (!!this.number && (!!this.number.match(/^[a-z0-9]+$/i) && this.number.length > 1)) {
        this.form_valid_map.number.isValid = true
        this.form_valid_map.number.showError = false;
        this.form_valid_map.number.errorTexts = [];
      } else {
        this.form_valid_map.number.isValid = false;
        this.form_valid_map.number.showError = showErrorIfDirty;
        this.form_valid_map.number.errorTexts = ["Kun 1 flybooking på samme ordre, kontakt os for grupper over 9 personer."];
      }
    },
    checkDestination() {
      if (!this.destination || !this.selectedDestination) {
        this.form_valid_map.destination.isValid = false
        this.form_valid_map.destination.errorTexts = ["Destination skal udfyldes"];
        return;
      }
      this.form_valid_map.destination.isValid = true
      this.form_valid_map.destination.errorTexts = [];
    },

    emitValid(){
      if(!this.invalid) {
        // this.$emit('valid'); 
      }
    },

    calculateDateWarning(date, time, should_trigger = false){
      if ((time.HH === "" || time.mm === "") && !should_trigger){
        return;
      }

      let calculated_date = date,
          timestamp = new Date().getTime() + (3 * 24 * 60 * 60 * 1000);

      if (time.HH !== "" && time.mm !== ""){
        this.form_valid_map.time.showError = false;
        //this.emitValid();
        let year = new Date(date).getFullYear(),
            month = new Date(date).getMonth(),
            day = new Date(date).getDate();

        calculated_date = new Date(year, month, day, time.HH, time.mm);
      }

      this.date_less_than_72_hours = timestamp > calculated_date;
    },
    setErrors(){
      if (!this.formState || !this.formState.$error) return;

      let errors = this.formState.$error;
      for (let key in errors) {
        if (errors.hasOwnProperty(key) && this.form_valid_map[key]) {
          this.form_valid_map[key].showError = errors[key].$invalid;
        }
      }

      if(this.time.HH === "" || this.time.mm === ""){
        this.form_valid_map.time.showError = true;
      }

      this.form_valid_map.date.showError = this.date == null;

    },

    getDestinationString :(destination) => destination.name + "(" + destination.iata + "), " + destination.cityDK + ", " + destination.countryDK,

    selectDestination(destination) {
      this.destination = this.getDestinationString(destination) + "(" + destination.countryCode + ")";
      this.iataCode = destination.iata;
      this.selectedDestination = destination;
      
      // If only Hjemrejse is selected, this destination is the departure airport
      if (this.serviceHjemrejse && !this.serviceUdrejse) {
        this.$store.state.bookingHome.departureAirport = destination.iata;
      }
      
      // Only disable serviceHjemrejse based on customs if both services are selected
      if (this.serviceUdrejse && this.serviceHjemrejse && destination.eucustoms === false) {
        this.serviceHjemrejse = false;
      }
    },

    clearDestination() {
      this.destination = null;
      this.iataCode = null;
      this.checkStepValid();
    },



    getAirLine(flight_number) {
      if (flight_number.length > 1 && this.validFlightNumber){
        var strTemp=flight_number.replace(/\s+/g, '');
        flight_number = strTemp.substring(0,2);//flight_number.substring(0, 2)
        return this.flight_companies[flight_number.replace(/\s+/g, '').substring(0, 2)] || null;
      }

      return null
    },
    validFlightNumber(flight_number) {
      if(flight_number !== null && flight_number.length > 1) {
        flight_number = flight_number.substring(0, 2)
        return this.flight_companies.hasOwnProperty(flight_number);
      }

      return true;
    },
    firstAvailableDate() {
      var d = new Date();
      let yr = d.toLocaleString('da-DK', {year: 'numeric',   timeZone: 'Europe/Copenhagen' }); //d.getFullYear();
      let m = d.toLocaleString('da-DK', {month: 'numeric',   timeZone: 'Europe/Copenhagen' })-1; //d.getMonth();
      let day = 1*d.toLocaleString('da-DK', {day: 'numeric',   timeZone: 'Europe/Copenhagen' }); //d.getHours();//d.getDate();
      let hh = 1*d.toLocaleString('da-Dk', {hour: 'numeric',   hour12: false, timeZone: 'Europe/Copenhagen' }); //d.getHours();
      //alert(yr + "-" + m + "-"+ (day+3) + ": " +hh);
      
      if (m >= 3 && m <= 9) {// High season from april to october
        if (hh > 11) { day+=4 } else { day+=3 } //Next available booking is approx 3½ day later, high season
      } else { // low season
        if (hh >= 10) { day+=3 } else { day+=2 } //Next available booking is approx 1½ day later, low season
      }
      return new Date(yr,m,day);
    },
    handleFlightNumberChange () {
      this.checkStepValid();
      let flight_number = this.flightNumber.toUpperCase();
      this.airline = this.getAirLine(flight_number);
    },

    handleCustomsAcceptanceChange(accepted) {
      this.customsAcceptance = accepted;
      this.checkStepValid();
    },

    handleCustomsValidationChange(isValid) {
      this.customsValidation = isValid;
      this.checkStepValid();
    },

    handleTravelTypeChange() {
      // Update the boolean fields based on the selected travel type
      this.isEuTravelOnly = this.travelType === 'eu';
      this.isNonEuTravel = this.travelType === 'non-eu';
      this.checkStepValid();
    },

    // Enhanced test mode detection
    shouldActivateTestMode() {
      const hostname = window.location.hostname;
      const isNetlifyPreview = hostname.includes('deploy-preview') || hostname.includes('netlify.app');
      const isLocalhost = hostname.includes('localhost') || hostname === '127.0.0.1';
      const isDevelopment = process.env.NODE_ENV === 'development';
      
      // Check for secret test parameter
      const hasTestParam = this.$route.query.test_mode === 'enable_skip_payment';
      
      // Check localStorage for persistent test mode (can be set via console)
      const hasTestStorage = localStorage.getItem('valizo_test_mode') === 'true';
      
      // Auto-activate on development environments OR when explicitly enabled
      return isNetlifyPreview || isLocalhost || isDevelopment || hasTestParam || hasTestStorage;
    }
  },

  mounted() {
    // Check airports data version first
    if (!checkAirportsDataVersion()) {
      return; // Page will reload, no need to continue
    }
    
    let rawAirports = null;
    try {
      rawAirports = getAirports();
    } catch (error) {
      console.error('Failed to load airports data:', error);
      this.airportsDataError = true;
      return;
    }

    if (!Array.isArray(rawAirports)) {
      console.error('Airports data is not an array:', rawAirports);
      this.airportsDataError = true;
      return;
    }

    const filteredAirports = rawAirports.filter((item) => {
      return item && typeof item === 'object' && item.name && item.iata && item.cityDK && item.countryDK;
    });
    this.airports = Object.freeze(filteredAirports);

    // Validate that airports data has required fields
    if (!validateAirportsData(this.airports) || this.airports.length === 0) {
      this.airportsDataError = true;
      return;
    }

    // Debug helper (enable with ?debug_airports=1)
    if (this.$route && this.$route.query && this.$route.query.debug_airports === '1') {
      window.valizoAirportsDebug = {
        total: rawAirports.length,
        valid: this.airports.length,
        sample: this.airports.slice(0, 3)
      };
    }
    
    page('/stepOne');

    this.selectedDestination = this.airports.find((el) => {
      return `${this.getDestinationString(el)}(${el.countryCode})` === this.destination;
    });

    this.checkStepValid();
  }
}
</script>

<style lang="scss">
  @use '../assets/scss/variables' as *;
  @use '../assets/scss/media-queries' as *;

  .flight-search {
    /*overflow: hidden*/
    position: relative;
  }

  .flight-search div {
    color: #768093;
    border: 1px solid #E2E2E2;
    max-height: 250px;
    position: absolute;
    top: 95px;
    background: white;
    overflow: scroll;
    padding: 5px 10px;
    width: 100%;
  }

  .flight-search span {
    padding: 5px;
    display: block;
  }

  .flight-search span:hover {
    opacity: 0.5;
    cursor: pointer;
  }

  .step-one {
    padding: 60px 0 0;

    .step {
      border-bottom: 0;
      padding: 60px 80px 40px;
      position: relative;

      @include max-screen(991px) {
        padding: 30px 30px 140px;
      }

      .back-btn {
        left: 0;
        bottom: 0;
      }
    }

    h2 {
      padding-left: 80px;
      margin-bottom: 60px;
    }

    .vc-container {
      font-family: 'Montserrat';
      font-size: 14px;
    }

    .time-picker {
      width: 100%;
      font-family: 'Montserrat';
      font-size: 14px;

      &.error-border input {
        border-color: red !important;
      }

      .display-time {
        padding: 18.5px;
        width: 100%;
        height: auto;
        border-color: $grey-1;
        outline: 0;
      }

      .dropdown {
        top: 59px;
        width: 100%;
        box-shadow: none;
        border: 1px solid $grey-1;
        border-right: 0;

        .select-list {
          width: auto;
          height: calc(10em - 2px);

          ul {
            li.active {
              background: $blue;
            }
          }
        }
      }
    }
    
    .include-home {
      padding: 60px 0px;

      @include max-screen(991px) {
        padding: 30px;
      }
    }
  }

  .checkbox-wrapper {
    display: flex;
    align-items: flex-start;
    
    input[type="checkbox"] {
      margin-top: 2px;
      margin-right: 8px;
    }
    
    label {
      flex: 1;
      line-height: 1.4;
    }
  }


  .radio-wrapper {
    display: flex;
    align-items: flex-start;
    
    input[type="radio"] {
      margin-top: 2px;
      margin-right: 8px;
    }
    
    label {
      flex: 1;
      line-height: 1.4;
    }
  }
</style>
